import { Button } from '@consigli/facade';
import { type ResponsibleParty } from '@consigli/types';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FaQuoteRight } from 'react-icons/fa';

import { formatMeetingDateTime } from './contradiction-utils';
import { SeverityBadge, getSeverityLinkStyles, type Severity } from './severity-badge';
import { formatResponsibleParties } from '../utils/participant-utils';

interface LinkedAgreementCardProps {
  agreementId: string;
  contradictionSeverity?: Severity | null;
  confidenceScore?: number | null;
  agreementText?: string | null;
  responsibleParties?: ResponsibleParty[] | null;
  meetingDatetime?: string | null;
  contradictionReason?: string | null;
  showAgreementLabel?: boolean;
  onShowSource?: () => void;
}

export const LinkedAgreementCard: FC<LinkedAgreementCardProps> = ({
  agreementId,
  contradictionSeverity,
  confidenceScore,
  agreementText,
  responsibleParties,
  meetingDatetime,
  contradictionReason,
  showAgreementLabel = false,
  onShowSource,
}) => {
  const { t } = useTranslation();
  const severity: Severity = contradictionSeverity || 'low';

  const severityLabels = {
    high: t('meeting-secretary.contradictions.severity-high'),
    medium: t('meeting-secretary.contradictions.severity-medium'),
    low: t('meeting-secretary.contradictions.severity-low'),
  };

  return (
    <div key={agreementId} className={`p-3 rounded border ${getSeverityLinkStyles(severity)}`}>
      <div className="flex items-center gap-2 mb-2">
        <SeverityBadge severity={severity} showLabel={false} severityLabels={severityLabels} />
        <span className="text-xs text-gray-500">
          {t('meeting-secretary.contradictions.confidence', {
            percent: Math.round((confidenceScore || 0) * 100),
          })}
        </span>
        {onShowSource && (
          <Button
            tertiary
            icon={FaQuoteRight}
            iconSize={12}
            onClick={(e) => {
              e.stopPropagation();
              onShowSource();
            }}
            className="ml-auto !py-0.5 !px-2 !text-xs text-meeting-700 hover:text-meeting-900"
            title={t('meeting-secretary.contradictions.view-in-transcript')}
          >
            {t('meeting-secretary.contradictions.view-in-transcript')}
          </Button>
        )}
      </div>

      {agreementText && (
        <div className="mb-2 p-2 bg-white/50 rounded">
          {showAgreementLabel && (
            <p className="text-xs text-gray-500 font-medium mb-1">
              {t('meeting-secretary.contradictions.meeting-agreement')}:
            </p>
          )}
          <p className="text-sm text-gray-800 font-medium">{agreementText}</p>
          {formatResponsibleParties(responsibleParties) && (
            <p className="text-xs text-meeting-600 mt-1">
              {t('meeting-secretary.contradictions.responsible', {
                parties: formatResponsibleParties(responsibleParties),
              })}
            </p>
          )}
          {meetingDatetime && (
            <p className="text-xs text-gray-500 mt-1">
              {t('meeting-secretary.contradictions.from-meeting-on', {
                date: formatMeetingDateTime(meetingDatetime),
              })}
            </p>
          )}
        </div>
      )}

      {contradictionReason && (
        <p className="text-sm text-gray-800">
          <span className="font-medium">
            {t('meeting-secretary.contradictions.why-contradicts')}{' '}
          </span>
          {contradictionReason}
        </p>
      )}
    </div>
  );
};
